import { getHitLabAttribution } from './hitLabUtils';

const API_BASE =
  process.env.REACT_APP_AZURE_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL_PROD ||
  '/api/v1';

export const HITLAB_FUNNEL_EVENTS = Object.freeze({
  LANDING_VIEWED: 'hitlab_landing_viewed',
  START_ARTIST_INTAKE_CLICKED: 'hitlab_start_artist_intake_clicked',
  AUTH_STARTED: 'hitlab_auth_started',
  INTAKE_VIEWED: 'hitlab_intake_viewed',
  ARTIST_PROFILE_COMPLETED: 'hitlab_artist_profile_completed',
  CHECKOUT_CANCELED: 'hitlab_checkout_canceled',
  CHECKOUT_RETURNED_SUCCESS: 'hitlab_checkout_returned_success',
  SUBMIT_VIEWED_PAID: 'hitlab_submit_viewed_paid',
});

const ALLOWED_EVENTS = new Set(Object.values(HITLAB_FUNNEL_EVENTS));
const JOURNEY_KEY = 'jg:hitlabJourneyId:v1';
const SENT_KEY = 'jg:hitlabTelemetrySent:v1';
const SAFE_HITLAB_PATHS = new Set([
  '/hitlab',
  '/hitlab/intake',
  '/hitlab/submit',
  '/hitlab/admin',
]);

const clean = (value, max = 256) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage || null;
  } catch (err) {
    return null;
  }
};

const readJson = (key, fallback) => {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const parsed = JSON.parse(storage.getItem(key) || '');
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (err) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // best effort only
  }
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `hitlab-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getHitLabJourneyId = () => {
  const storage = getStorage();
  if (!storage) return randomId();
  const existing = clean(storage.getItem(JOURNEY_KEY), 128);
  if (existing) return existing;
  const next = randomId();
  try {
    storage.setItem(JOURNEY_KEY, next);
  } catch (err) {
    // best effort only
  }
  return next;
};

export const getHitLabTelemetryContext = () => ({
  journeyId: getHitLabJourneyId(),
});

const normalizeHitLabPath = (path = '') => {
  const raw = clean(path, 160);
  if (!raw) return '';
  let pathname = raw;
  try {
    pathname = new URL(raw, 'https://www.joegalaxy.net').pathname;
  } catch (err) {
    pathname = raw.split('?')[0].split('#')[0];
  }
  return SAFE_HITLAB_PATHS.has(pathname) ? pathname : '';
};

const normalizeStatus = (value) =>
  clean(value, 80)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

const buildEndpoint = () =>
  `${String(API_BASE || '/api/v1').replace(/\/+$/, '')}/hitlab/events`;

const buildEventKey = ({ eventName, journeyId, path, status }) =>
  ['hitlab', eventName, journeyId, path || '', status || ''].join(':');

export const buildHitLabTelemetryPayload = ({
  eventName,
  search = '',
  path = '',
  status = '',
} = {}) => {
  if (!ALLOWED_EVENTS.has(eventName)) return null;
  const attribution = getHitLabAttribution(search);
  const safePath = normalizeHitLabPath(path);
  const safeStatus = normalizeStatus(status);
  return {
    eventName,
    journeyId: getHitLabJourneyId(),
    ...attribution,
    path: safePath || undefined,
    status: safeStatus || undefined,
  };
};

export const recordHitLabEvent = async (
  eventName,
  { search = '', path = '', status = '', dedupe = true } = {},
) => {
  const payload = buildHitLabTelemetryPayload({
    eventName,
    search,
    path,
    status,
  });
  if (!payload || typeof fetch !== 'function') return false;

  const eventKey = buildEventKey(payload);
  const sent = readJson(SENT_KEY, {});
  if (dedupe && sent[eventKey]) return false;

  try {
    const response = await fetch(buildEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    });
    if (!response?.ok) return false;
    if (dedupe) {
      sent[eventKey] = Date.now();
      writeJson(SENT_KEY, sent);
    }
    return true;
  } catch (err) {
    return false;
  }
};
